import { LineItemData, OrderStatus, OrderType } from "~/gql/graphqlBase.generated";
import { type InvoiceStandingFields, isRebillable } from "~fulfill/orders/helpers/invoiceEligibility";
import { billedOrderedEaches } from "~fulfill/orders/helpers/orderLineUnits";

const viewOnlyStatuses: OrderStatus[] = [
	OrderStatus.Shipped,
	OrderStatus.InTransit,
	OrderStatus.Delivered,
	OrderStatus.FailedDelivery,
	OrderStatus.Cancelled,
	OrderStatus.Deleted,
	OrderStatus.Synchronized,
	OrderStatus.Closed
];

const allocationEditableStatuses: OrderStatus[] = [
	OrderStatus.Open,
	OrderStatus.Initialized,
	OrderStatus.Submitted,
	OrderStatus.Acknowledged,
	OrderStatus.Picked
];

export const isAllocationEditable = (orderStatus: OrderStatus | undefined): boolean =>
	orderStatus != null && allocationEditableStatuses.includes(orderStatus);

// wsapi AllowsAddingLineItems: draft, plus active except Shipped. Override price and delete share it.
const lineItemEditableStatuses: OrderStatus[] = [
	OrderStatus.Initialized,
	OrderStatus.Open,
	OrderStatus.Submitted,
	OrderStatus.Acknowledged,
	OrderStatus.Picked
];

const isLineItemEditable = (orderStatus: OrderStatus | undefined): boolean =>
	orderStatus != null && lineItemEditableStatuses.includes(orderStatus);

// Narrower than wsapi AllowsUpdatingLineItemQuantity by product ruling: ordered quantity locks at Submit.
const orderedQuantityEditableStatuses: OrderStatus[] = [OrderStatus.Initialized, OrderStatus.Open];

export const isOrderedQuantityEditable = (orderStatus: OrderStatus | undefined): boolean =>
	orderStatus != null && orderedQuantityEditableStatuses.includes(orderStatus);

export const isEachesQuantityEditable = isOrderedQuantityEditable;

const quoteDetailsEditableStatuses: OrderStatus[] = [OrderStatus.Initialized, OrderStatus.Open];

export const isQuoteDetailsEditable = (orderStatus: OrderStatus | undefined): boolean =>
	orderStatus != null && quoteDetailsEditableStatuses.includes(orderStatus);

const isLineOnOrder = (row: Pick<LineItemData, "lineNumber"> | undefined): boolean => Boolean(row?.lineNumber);

export const isItemsTableQuantityEditable = (
	orderStatus: OrderStatus | undefined,
	row: Pick<LineItemData, "lineNumber"> | undefined
): boolean => !isLineOnOrder(row) || isOrderedQuantityEditable(orderStatus);

export const allowsAddingLineItems = isLineItemEditable;

export const addLineItemsBlockedReasonKey = (orderStatus: OrderStatus | undefined) => {
	if (allowsAddingLineItems(orderStatus)) return undefined;
	return orderStatus === OrderStatus.SyncError
		? "orders.ribbon.disabled.syncErrorAddLine"
		: "orders.ribbon.disabled.notEditable";
};

export const isOverridePriceEditable = isLineItemEditable;

export const isLineItemDeletable = isLineItemEditable;

export const isLineCustomerItemEditable = isLineItemEditable;

const acknowledgedQuantityEditableStatuses: OrderStatus[] = [
	OrderStatus.Submitted,
	OrderStatus.Acknowledged,
	OrderStatus.Picked
];

export const isAcknowledgedQuantityEditable = (orderStatus: OrderStatus | undefined): boolean =>
	orderStatus != null && acknowledgedQuantityEditableStatuses.includes(orderStatus);

const shippedQuantityEditableStatuses: OrderStatus[] = [OrderStatus.Picked, OrderStatus.Shipped];

export const isShippedQuantityEditable = (orderStatus: OrderStatus | undefined): boolean =>
	orderStatus != null && shippedQuantityEditableStatuses.includes(orderStatus);

export const isAcknowledgedQuantityEdited = (
	lineItem: LineItemData | undefined,
	orderStatus?: OrderStatus
): boolean => {
	if (lineItem?.acknowledgedQuantity == null) return false;
	const isBilledView = orderStatus === OrderStatus.Acknowledged || orderStatus === OrderStatus.Picked;
	return lineItem.acknowledgedQuantity !== (isBilledView ? billedOrderedEaches(lineItem) : lineItem.orderedQuantity);
};

export interface StoredAcknowledgement {
	acknowledgedQuantity?: null | number;
	acknowledgedZeroBillsNothing?: boolean | null;
	isStaged?: boolean;
}

export interface AcknowledgedQuantityEntry extends StoredAcknowledgement {
	currentEaches: null | number;
	enteredEaches: null | number;
	isTouched: boolean;
}

export const offersAcknowledgedZeroReentry = ({
	acknowledgedQuantity,
	acknowledgedZeroBillsNothing,
	isStaged
}: StoredAcknowledgement): boolean => acknowledgedQuantity === 0 && !acknowledgedZeroBillsNothing && !isStaged;

export const isAcknowledgedEntryDirty = ({
	currentEaches,
	enteredEaches,
	isTouched,
	...stored
}: AcknowledgedQuantityEntry): boolean =>
	enteredEaches !== currentEaches || (isTouched && enteredEaches === 0 && offersAcknowledgedZeroReentry(stored));

export const isShippedQuantityEdited = (
	lineItem: LineItemData | undefined,
	serverLineItems: LineItemData[] | undefined
): boolean => lineItem?.shippedQuantity !== serverLineItems?.find((item) => item.id === lineItem?.id)?.shippedQuantity;

const isOrderReadOnly = (orderStatus: OrderStatus | undefined): boolean =>
	orderStatus != null && viewOnlyStatuses.includes(orderStatus);

export type ShipFromPickerMode = "editable" | "readOnly" | "unallocationConfirmRequired";

export const resolveShipFromPickerMode = ({
	allocationCount,
	orderStatus
}: {
	allocationCount: number;
	orderStatus: OrderStatus | undefined;
}): ShipFromPickerMode => {
	if (isOrderReadOnly(orderStatus)) return "readOnly";
	return allocationCount > 0 ? "unallocationConfirmRequired" : "editable";
};

const shippingUomSwitchStatuses: OrderStatus[] = [OrderStatus.Initialized, OrderStatus.Open, OrderStatus.Submitted];

// wsapi enforces the same switch window.
export const isShippingUomEditable = (
	orderStatus: OrderStatus | undefined,
	hasShippingUomChangeHandler: boolean,
	isLineShippingUomEditable: boolean
): boolean =>
	orderStatus != null &&
	shippingUomSwitchStatuses.includes(orderStatus) &&
	hasShippingUomChangeHandler &&
	isLineShippingUomEditable;

const noTaxLockedStatuses: OrderStatus[] = [OrderStatus.Synchronized, OrderStatus.Cancelled, OrderStatus.Deleted];

const erpBilledStatuses: OrderStatus[] = [
	OrderStatus.Closed,
	OrderStatus.InTransit,
	OrderStatus.Delivered,
	OrderStatus.FailedDelivery
];

export interface NoTaxEditabilityFields extends InvoiceStandingFields {
	orderId?: string;
	status?: OrderStatus;
	syncAttempts?: ReadonlyArray<{ success: boolean }>;
	type?: OrderType;
}

const isBilledInErp = (order: NoTaxEditabilityFields): boolean =>
	order.status != null &&
	erpBilledStatuses.includes(order.status) &&
	(order.syncAttempts ?? []).some((attempt) => attempt.success);

export const canEditOrderNoTax = (order: NoTaxEditabilityFields | undefined): boolean =>
	Boolean(order?.orderId) &&
	order?.status != null &&
	!noTaxLockedStatuses.includes(order.status) &&
	!isBilledInErp(order) &&
	order.type !== OrderType.Credit &&
	isRebillable(order);

export default isOrderReadOnly;
